import { useEffect, useRef } from 'react';

// Elements considered "in the tab order" for trapping purposes. Deliberately
// excludes tabindex="-1" (roving-tabindex children of Menu manage themselves).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * Shared focus-trap behaviour for Dialog, Drawer and Popover (spec §7.2, §12).
 *
 * While `active` is true this:
 *   - remembers the element that had focus and moves focus into the surface,
 *   - traps Tab / Shift+Tab so focus cannot escape the surface,
 *   - calls `onClose` when Escape is pressed,
 *   - restores focus to the invoking element once `active` becomes false,
 *   - locks body scroll (unless `lockScroll` is explicitly disabled — Popover
 *     opts out because it is not a modal surface).
 *
 * @param {object} options
 * @param {boolean} options.active - whether the surface is currently open.
 * @param {import('react').RefObject<HTMLElement>} options.containerRef - the
 *   surface node to trap focus within.
 * @param {() => void} [options.onClose] - invoked on Escape.
 * @param {import('react').RefObject<HTMLElement>} [options.initialFocusRef] -
 *   element to focus first; falls back to the first focusable descendant,
 *   then the container itself.
 * @param {boolean} [options.lockScroll=true] - lock `document.body` scrolling
 *   while active.
 */
export function useFocusTrap({
  active,
  containerRef,
  onClose,
  initialFocusRef,
  lockScroll = true
}) {
  const invokerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    invokerRef.current = document.activeElement;
    const container = containerRef.current;

    // Entrance animations mount the surface a frame before it is painted in
    // its final position; wait for that so focus doesn't jump mid-transition.
    const raf = requestAnimationFrame(() => {
      const target = initialFocusRef?.current || container?.querySelector(FOCUSABLE_SELECTOR) || container;
      target?.focus?.();
    });

    const previousOverflow = document.body.style.overflow;
    if (lockScroll) {
      document.body.style.overflow = 'hidden';
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab' || !container) return;

      const focusable = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        // Nothing to tab to — keep focus pinned on the surface itself.
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Capture phase: Escape must close the topmost surface before it can
    // reach anything the surface itself is layered on top of.
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (lockScroll) {
        document.body.style.overflow = previousOverflow;
      }
      invokerRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

/**
 * Combines a forwarded ref with an internal one so overlay components can
 * both expose a DOM node to consumers and keep the ref they need internally
 * (focus-trap container, position measurement, ...).
 *
 * @param {...(import('react').Ref<any>|undefined)} refs
 * @returns {(node: any) => void}
 */
export function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(node);
      else ref.current = node;
    });
  };
}

export default useFocusTrap;
